// @noflow
/* global Uint8Array */
import React, { Component } from 'react';
import { connect } from 'react-redux';
import cookie from 'react-cookies';
import { isEmpty, first, throttle } from 'lodash';
import { getNextEpisode, BINGE_WATCHING_PERIOD } from 'Common/bingewatching/Utils';
import { SourceType } from 'Common/models/franchise';
import {
  cancelNextBingeEpisode,
  showBingeMessage,
  isNextBingeEpisodeLoading,
} from 'Common/player/actions';
import { setFranchiseProgress } from 'Common/franchise/actions';
import {
  analyticsNumberOfContinuousEpisodesWatched,
  analyticsBingeWatchingNoUserAction,
} from 'Common/analytics/sharedAnalytics/actions';
import { Box } from 'Common/components';
import withPlayer from 'Common/player/withPlayer';
import Stream from 'Common/models/stream';
import AiringEventListing from 'Common/models/AiringEventListing';
import BitmovinDelegate from 'Common/lib/player/BitmovinDelegate';
import PlayerManagers from 'Common/lib/player/PlayerManagers';
import ConvivaManager from 'Common/lib/player/managers/ConvivaManager';
import ComscoreManager from 'Common/lib/player/managers/ComscoreManager';
import OmnitureManager from 'Common/lib/player/managers/OmnitureManager';
import NielsenDCRManager from 'Common/lib/player/managers/NielsenDCRManager';
import NielsenDTVRManager from 'Common/lib/player/managers/NielsenDTVRManager';
import StatspostManager from 'Common/lib/player/managers/StatspostManager';
import BlackoutManager from 'Common/lib/player/managers/BlackoutManager';
import OnDemandAdManager from 'Common/lib/player/managers/OnDemandAdManager';
import LiveLinearAdManager from 'Common/lib/player/managers/LiveLinearAdManager';
import PlayerErrorMessage from 'Common/player/PlayerErrorMessage';
import BingeWatchingMessage from './BingeWatchingMessage';

import { BitmovinPlayer, PlayerEvent } from './BitmovinPlayer';

export const styles = {
  container: {
    height: '100%',
    '> .html5-player': {
      height: '100%',
    },
    '> .html5-player #bitmovinplayer-video-player': {},
    '> .html5-player .bmpui-ui-fullscreentogglebutton.bmpui-on': {
      backgroundImage: `url(${require('./images/full-screen.svg')})`,
    },
    '> .html5-player .bmpui-seekbar-label-inner': {
      borderBottom: '2px solid #e41932',
    },
    '> .html5-player .bmpui-seekbar-label-inner:after': {
      borderTopColor: '#e41932',
    },
    '> .html5-player .bmpui-seekbar-playbackposition': {
      backgroundColor: '#e41932',
    },
    '> .html5-player .bmpui-ui-buffering-overlay-indicator': {
      backgroundImage: `url(${require('./images/overlay-indicator.svg')})`,
    },
    '> .html5-player .bmpui-image': {
      backgroundImage: `url(${require('./images/play-circle.svg')})`,
    },
    '> .html5-player .bmpui-seekbar .bmpui-seekbar-playbackposition-marker': {
      backgroundImage: `url(${require('./images/position-marker.svg')})`,
    },
    '> .html5-player .bmpui-ui-selectbox': {
      color: 'white',
      fontSize: '.8em',
    },
    '> .html5-player .bmpui-ui-settingstogglebutton.bmpui-on': {
      backgroundImage: `url(${require('./images/settings-toggle-button.svg')})`,
    },
    '> .html5-player .bmpui-ui-watermark': {
      backgroundImage: `url(${require('../components/navigation/img/dish_anywhere_logo.png')})`,
      backgroundSize: '66px 43px',
      opacity: 1,
      transition: 'opacity .3s ease-in-out',
      pointerEvents: 'none',
    },
    '> .html5-player .bmpui-player-state-playing .bmpui-ui-watermark': {
      opacity: 0,
    },
    '> .aspect-16x9:before': {
      paddingBottom: '0px !important',
    },
    '@media (max-width: 800px)': {
      '> .aspect-16x9:before': {
        marginTop: -200,
      },
    },
    '> .html5-player .bmpui-container-wrapper > .bmpui-ui-settings-panel': {
      '@media (max-width: 800px)': {
        backgroundColor: '#181818',
        width: '100%',
        height: '100%',
        zIndex: '9',
        overflow: 'scroll',
        opacity: '.95',
        position: 'absolute',
        top: 0,
        left: 0,
      },
    },
    '> .html5-player .bmpui-ui-skin-smallscreen .bmpui-ui-settings-panel > .bmpui-container-wrapper': {
      '@media (max-width: 800px)': {
        width: '100%',
        padding: 10,
        fontSize: '1.6em',
      },
    },
    '> .html5-player .bmpui-ui-skin-smallscreen .bmpui-ui-settings-panel .bmpui-ui-closebutton': {
      '@media (max-width: 800px)': {
        margin: 0,
        top: 5,
        right: 5,
        border: '1px solid #333',
      },
    },
    '> .html5-player .bmpui-ui-skin-smallscreen .bmpui-ui-titlebar >.bmpui-container-wrapper': {
      '@media (max-width: 800px)': {
        display: 'block',
      },
    },
  },
};

export type Props = {
  accountZipCode: string,
  autoPlay: boolean,
  initAustin: boolean,
  maximumBitRate: number,
  name: string,
  onPlayerEvent: Function,
  playerKey: string,
  qvtProxyEnabled: boolean,
  stream: Stream,
  title: string,
  type: string,
};

class Player extends Component<Props> {
  updateProgressInterval: IntervalID;
  checkLoggedInStateInterval: IntervalID;
  setupBlackedOutPlayerInterval: IntervalID;
  playerDelegate: BitmovinDelegate;
  playerView: BitmovinDelegate;

  static defaultProps = {
    autoPlay: true,
  };

  async componentDidMount() {
    const { stream, postViewContent, fetchVideoMetadata } = this.props;
    const { product_id: productId, pricing_plan_id: pricingPlanId, video_id: videoId } =
      stream || {};
    if (!this.isLiveLinear && videoId) {
      await fetchVideoMetadata(videoId);
    }

    if (this.isLiveLinear) {
      await this.qvtHeartbeatFetch();
    }

    if (productId && pricingPlanId) {
      postViewContent({
        productId,
        pricingPlanId,
      });
      this.sendEstProgressInterval = setInterval(this.sendEstProgress, 5000);
    }

    if (!this.isLiveLinear) {
      clearInterval(this.initialRecentsProgressInterval);
      this.initialRecentsProgressInterval = setInterval(this.sendInitialRecentsProgress, 1000);
    }

    this.initPlayer();

    this._onTimeChanged = throttle(this._onTimeChanged, 1000);

    if (typeof window !== 'undefined') {
      window.scrollTo({
        top: 0,
        left: 0,
        behavior: 'smooth',
      });

      // Bitmovin Ad Manager is downloaded on page load
      // It needs a player element to attach to, so we wait until the player is mounted, then attach it
      const bitmovinAdModule = window.bitmovin.player['advertising-bitmovin'].default;
      const { player } = document.getElementById('player');
      player.constructor.addModule(bitmovinAdModule);
    }
  }

  componentDidUpdate(prevProps) {
    const { stream = {}, postViewContent, sourceType } = this.props;
    const { sourceType: prevSourceType } = prevProps;

    if (prevSourceType !== sourceType) {
      this.reInitEvents();
      this.initManagers();
    }
    if (
      (stream?.qvtUrl !== prevProps.stream?.qvtUrl ||
        stream?.hls_guid !== prevProps.stream?.hls_guid) &&
      !isEmpty(stream)
    ) {
      this.updateStream();
      if (!this.isLiveLinear) {
        clearInterval(this.sendRecentsProgressInterval);
        clearInterval(this.initialRecentsProgressInterval);

        this.initialRecentsProgressInterval = setInterval(this.sendInitialRecentsProgress, 1000);

        const { product_id: productId, pricing_plan_id: pricingPlanId } = stream;
        if (productId && pricingPlanId) {
          clearInterval(this.sendEstProgressInterval);
          postViewContent({
            productId,
            pricingPlanId,
          });
          this.sendEstProgressInterval = setInterval(this.sendEstProgress, 5000);
        }
      }
    }
  }

  async componentWillUnmount() {
    const {
      callBitmovinApi,
      stream,
      franchise,
      franchiseSlug,
      analyticsNumberOfContinuousEpisodesWatched,
    } = this.props;

    if (stream && stream.product_id && stream.pricing_plan_id) {
      this.sendEstProgress({ viewingComplete: true });
    }

    await callBitmovinApi('unload');
    this.clearIntervals();
    if (this.managers) {
      this.managers.destroy();
    }
    this.destroyPlayer();

    if (this.isOnDemand && franchise && this.playNextEpisodeCount > 0) {
      const franchiseInfo = franchise[franchiseSlug] || {};
      analyticsNumberOfContinuousEpisodesWatched(
        franchiseInfo.networks[0].name || '',
        franchiseInfo.franchiseName || '',
        this.playNextEpisodeCount,
      );
    }
  }

  sendEstProgress = (streamingComplete = false, viewingComplete = false) => {
    const { viewContentReference, stream, updateContentProgress } = this.props;
    // Find and fix root cause
    if (isEmpty(viewContentReference)) {
      return;
    }
    const { ViewContentReference } = viewContentReference;
    const bitmovinPlayer = this.playerRef.current?.player;

    if (!bitmovinPlayer || !stream) {
      return;
    }

    const { product_id: productId, pricing_plan_id: pricingPlanId } = stream;

    if (!productId || !pricingPlanId) {
      console.warn('Purchase stream does not have productId or pricingPlanId');
      return;
    }

    const totalDuration = Math.floor(bitmovinPlayer.getDuration());
    const progressSeconds = Math.floor(bitmovinPlayer.getCurrentTime());

    updateContentProgress({
      productId,
      pricingPlanId,
      progressSeconds,
      viewContentReference: ViewContentReference,
      streamingComplete: totalDuration - progressSeconds <= 5 || streamingComplete,
      viewingComplete,
    });
  };

  managers = undefined;
  containerRef = React.createRef();
  playerRef = React.createRef();
  isAdBreak = false;
  playNextEpisodeCount = 0;
  progress = 0;

  get isLiveLinear() {
    const { sourceType } = this.props;
    return sourceType === SourceType.LIVE_LINEAR;
  }

  get isOnDemand() {
    const { sourceType } = this.props;
    return sourceType === SourceType.ON_DEMAND;
  }

  get isEst() {
    const { sourceType } = this.props;
    return sourceType === SourceType.EST;
  }

  get isSafari() {
    return navigator.userAgent.match(/safari/i) && !navigator.userAgent.match(/chrome/i);
  }

  get events() {
    if (this.isLiveLinear) {
      return {
        [PlayerEvent.SourceLoaded]: (...args) => {
          this._onSourceLoaded(...args);
          if (this.transitionedLinearAsset) {
            // if you start playing a LL asset, you don't want it to shift back in time
            // on transition between them, you do, especially in case you scrubbed back before transition
            this.playerView.timeShift(this.playerView.getMaxTimeShift());
            this.transitionedLinearAsset = false;
          }
        },
        [PlayerEvent.Play]: () => {
          if (!this.transitionedLinearAsset && this.isSafari) {
            this.playerView.timeShift(0);
          }
        },
        [PlayerEvent.PlaybackFinished]: (...args) => {
          this._onPlaybackFinished(...args);
          this.transitionedLinearAsset = true;
        },
        [PlayerEvent.Error]: (...args) => {
          this._onPlayerError(...args);
        },
      };
    }
    if (this.isOnDemand || this.isEst) {
      return {
        [PlayerEvent.Seek]: () => {
          this._onSeek();
        },
        [PlayerEvent.SourceUnloaded]: () => {
          const { setFranchiseProgress, stream } = this.props;
          const duration = stream?.total_seconds || stream?.run_time;
          const progressPercentage = Math.round((this.progress / duration) * 100);
          setFranchiseProgress(progressPercentage, this.progress);
        },
        [PlayerEvent.PlaybackFinished]: () => {
          this.clearIntervals();
        },
        [PlayerEvent.Error]: (...args) => {
          this._onPlayerError(...args);
        },
        [PlayerEvent.TimeChanged]: (...args) => {
          this._onTimeChanged(...args);
          this.setProgress(...args);
        },
        [PlayerEvent.AdBreakStarted]: () => {
          this.isAdBreak = true;
        },
        [PlayerEvent.AdBreakFinished]: () => {
          this.isAdBreak = false;
        },
      };
    }
    return {
      [PlayerEvent.PlaybackFinished]: () => {
        this.clearIntervals();
      },
      [PlayerEvent.Error]: () => {
        this.clearIntervals();
      },
    };
  }

  get playerConfig() {
    const { playerKey, playerUiCss, playerUiJs } = this.props;

    return {
      key: playerKey,
      advertising: {
        strategy: {
          shouldPlayAdBreak: () => true,
        },
      },
      cast: {
        enable: false,
      },
      location: {
        ui: '//cdn.bitmovin.com/player/web/8.40.0/bitmovinplayer-ui.js',
        ui_css: '//cdn.bitmovin.com/player/web/8.40.0/bitmovinplayer-ui.css',
      },
    };
  }

  get playerSource() {
    const { stream, title, appVersion, radishUrl, networkDetails, channelInfo, env } = this.props;
    const { drm, source } = stream || {};
    const { fairplay, playready, widevine } = drm || {};
    const { dash, hls, progressive } = source || {};
    const dashRealPto = dash ? dash.replace('.mpd', '-real-pto.mpd') : '';
    const playerSource = {
      dash: dashRealPto,
      hls,
      progressive,
      title: undefined,
      drm: undefined,
    };
    const getCallsign = () =>
      this.isLiveLinear && channelInfo?.linear_callsign
        ? channelInfo.linear_callsign
        : channelInfo?.callsign || networkDetails?.network_player?.callsigns;

    const version = appVersion.substring(0, 8);

    if (!isEmpty(title)) {
      playerSource.title = title;
    }

    if (!isEmpty(dash) || !isEmpty(hls)) {
      playerSource.drm = {
        fairplay: {
          certificateURL: `${fairplay}/certificate`,
          prepareMessage: event => event.message,
          prepareContentId: contentId => {
            const link = document.createElement('a');
            link.href = contentId.substring(2);
            return link.hostname;
          },
          getLicenseServerUrl: t => {
            const contentId = t.substring(t.length - 32);
            return `${fairplay}/${contentId}`;
          },
          prepareLicense: license => new Uint8Array(license),
          headers: {
            // eslint-disable-next-line camelcase
            'Asset-Id': stream?.video_id?.toString() || stream?.ruid?.toString(),
            'User-Id': cookie.load('rs'),
            'Channel-Id': getCallsign(),
            Env: env,
            'Radish-Host': radishUrl,
          },
          useUint16InitData: true,
          licenseResponseType: 'arraybuffer',
        },
        playready: {
          LA_URL: playready,
        },
        widevine: {
          LA_URL: widevine,
          prepareMessage: ({ message }) => {
            const ret = {
              client: `DishAnywhere-Web/${version}`,
              // this is the session id for radish
              // user_id: "0925766e-db9b-11e7-aede-0a3eecf5965a",
              user_id: cookie.load('rs') ? cookie.load('rs') : 1234,
              // the backend to make radish requests
              radish_host: radishUrl,
              // the widevine request, this may be the base64 encoded representation or
              // the array encoded representation. base64 is preferred.
              message: btoa(String.fromCharCode.apply(null, new Uint8Array(message))),
            };
            if (this.isLiveLinear) {
              // Per dave, this is the channel name that he wants, not the channel GUID.
              // for linear, this is the channel guid, for vod, this is omitted
              // if the radish endpoint doesn't differentiate between a channel or asset,
              // it is permissible to just use asset_id
              // channel_id: "904e41a22a9a43a5a4d9d160cbb29f03",
              ret.channel_id = getCallsign();
            } else if (this.isOnDemand) {
              // only one of asset_id or channel_id is permitted
              // for VOD this is the video id, for linear, this is omitted
              // asset_id: "904e41a22a9a43a5a4d9d160cbb29f03",
              ret.asset_id = stream.video_id.toString();
            } else if (this.isEst) {
              // for EST we pass the ruid due to it being the only unique ID on the payload
              ret.asset_id = stream.ruid.toString();
            }

            return JSON.stringify(ret);
          },
          licenseRequestRetryDelay: 5,
          maxLicenseRequestRetries: 3,
        },
      };
    }
    return playerSource;
  }

  clearIntervals() {
    const { clearQvtInterval } = this.props;
    clearInterval(this.sendRecentsProgressInterval);
    clearInterval(this.initialRecentsProgressInterval);
    clearInterval(this.sendEstProgressInterval);
    clearInterval(this.checkLoggedInStateInterval);
    clearInterval(this.setupBlackedOutPlayerInterval);
    clearQvtInterval();
  }

  initManagers = () => {
    const {
      accountZipCode,
      blackoutVisible,
      videoMetadata,
      adaptUrl,
      onDemandListing,
      airings,
      browserFingerprint,
      viewer,
      userIp,
      stream,
    } = this.props;
    const { analytics_id: analyticsId } = viewer;

    this.playerDelegate = new BitmovinDelegate(this);

    if (!isEmpty(videoMetadata)) {
      let managersForStream;
      if (this.isLiveLinear) {
        const firstLinearAiring =
          stream || airings?.find(airing => airing.airing_type === 'linear') || {};
        managersForStream = [
          new StatspostManager(this.playerDelegate), // not setup
          new BlackoutManager(this.playerDelegate, blackoutVisible, accountZipCode),
          new NielsenDTVRManager(this.playerDelegate),
          new OmnitureManager(this.playerDelegate),
          new ConvivaManager(this.playerDelegate, firstLinearAiring, browserFingerprint),
          new ComscoreManager(this.playerDelegate),
          new LiveLinearAdManager(this.playerDelegate, adaptUrl, analyticsId, userIp),
        ];
      } else if (this.isOnDemand) {
        managersForStream = [
          new ConvivaManager(this.playerDelegate, onDemandListing, browserFingerprint),
          new ComscoreManager(this.playerDelegate),
          new OmnitureManager(this.playerDelegate),
          new NielsenDCRManager(this.playerDelegate),
          new OnDemandAdManager(this.playerDelegate, analyticsId, userIp, adaptUrl),
        ];
      } else {
        return;
      }
      this.managers = new PlayerManagers(managersForStream);
    }
  };

  qvtHeartbeatFetch = (next = false) => {
    const { fetchQvt, stream, qvtPayload } = this.props;
    const qvtUrl = stream.qvt_url || stream.qvtUrl;

    if (next) {
      const { _next: nextQvtUrl } = qvtPayload;
      return fetchQvt(nextQvtUrl, true);
    }
    return fetchQvt(qvtUrl);
  };

  _onSourceLoaded() {
    const { airings, fetchAiringsData, fetchLiveVideoMetadata, stream } = this.props;

    if (!this.isLiveLinear) return;

    this.qvtHeartbeatFetch();

    if (airings && !isEmpty(airings)) {
      const currentAiring = first(
        airings.filter(airing => {
          const streamQvtUrl = stream.qvt_url || stream.qvtUrl;
          const airingQvtUrl = airing.qvt_url || airing.qvtUrl;
          return streamQvtUrl === airingQvtUrl;
        }),
      );
      const { ott_mapping_id: ottMappingId } = currentAiring;
      fetchAiringsData(ottMappingId);
      fetchLiveVideoMetadata(ottMappingId);
    }
  }

  initPlayer() {
    const { setPlayerApi } = this.props;

    this.playerView = new BitmovinPlayer(this.playerRef.current, this.playerConfig);
    setPlayerApi(this.playerView);

    this.initAutoPlay();
    this.initEvents();
    this.initBroadcast();
    this.loadStream();
    this.initManagers();
  }

  initAutoPlay() {
    const { autoPlay } = this.props;

    if (autoPlay) {
      this.playerView.on(PlayerEvent.SourceLoaded, () => {
        this.play();
      });
    }
  }

  initBroadcast() {
    const { playerEvents } = this.props;
    playerEvents.on('austin', this.handleAustinEvent);
  }

  destroyBroadcast() {
    const { playerEvents } = this.props;
    playerEvents.off('austin', this.handleAustinEvent);
  }

  sendInitialRecentsProgress = async () => {
    const { postToRecents, contentId } = this.props;

    const { currentTime = 0, duration = 0 } = this.playerDelegate || {};

    if (this.isAdBreak) {
      return;
    }

    if (currentTime !== 0 && duration !== 0 && contentId) {
      const recentsData = {
        contentSource: 'ott',
        content_id: contentId,
        total_seconds: Math.floor(duration),
        progress_seconds: Math.floor(currentTime),
      };

      clearInterval(this.initialRecentsProgressInterval);
      await postToRecents(recentsData);
      this.sendRecentsProgressInterval = setInterval(this.sendRecentsProgress, 5000);
    }
  };

  sendRecentsProgress = () => {
    const { recentVideoId, putToRecents, putToRecentsError, putToRecentsFinished } = this.props;

    if (!this.playerDelegate || !recentVideoId || this.isAdBreak) return undefined;

    const recentsData = {
      progressSeconds: Math.floor(this.playerDelegate.currentTime),
      totalSeconds: Math.floor(this.playerDelegate.duration),
    };
    if (!putToRecentsFinished && isEmpty(putToRecentsError)) {
      return putToRecents(recentVideoId, recentsData);
    }

    clearInterval(this.sendRecentsProgressInterval);
    this.sendRecentsProgressInterval = undefined;
  };

  handleAustinEvent = event => {
    console.log('player handleAustinEvent', event);

    if (event.fn && this.playerView[event.fn]) {
      const { fn, args } = event;

      if (fn === 'setPlaybackSpeed') {
        const [playbackRate] = args;
        return this.playerView.setPlaybackSpeed(playbackRate);
      }

      if (fn === 'seek') {
        const [seconds] = args;
        return this.playerView.seek(seconds);
      }
      console.warn('bitmovin player unhandled fn', fn, args);
      const eventArgs = args || [];
      console.log(`apply bitmovinplayer.${fn}(${eventArgs.join(', ')})`);
      return this.playerView[event.fn].apply(this.playerView, eventArgs);
    }
    console.warn('bitmovin player unhandled fn event', event);

    console.warn('bitmovin player unhandled fn event', event);
  };

  playerEvents = [
    PlayerEvent.AdBreakStarted,
    PlayerEvent.AdBreakFinished,
    PlayerEvent.Error,
    PlayerEvent.Warning,
    PlayerEvent.Ready,
    PlayerEvent.SourceLoaded,
    PlayerEvent.SourceUnloaded,
    PlayerEvent.StallStarted,
    PlayerEvent.StallEnded,
    PlayerEvent.TimeChanged,
    PlayerEvent.DownloadFinished,
    PlayerEvent.Metadata,
    PlayerEvent.Play,
    PlayerEvent.Paused,
    PlayerEvent.Playing,
    PlayerEvent.PlaybackFinished,
    PlayerEvent.SubtitleAdded,
    PlayerEvent.SubtitleDisabled,
    PlayerEvent.TimeChanged,
    PlayerEvent.CueEnter,
    PlayerEvent.CueExit,
    PlayerEvent.PlayerResized,
    PlayerEvent.Seek,
    PlayerEvent.Seeked,
    PlayerEvent.Muted,
  ];

  initEvents() {
    this.playerEvents.forEach(eventType => this.playerView.on(eventType, this.handlePlayerEvent));
  }

  reInitEvents = () => {
    this.playerEvents.forEach(eventType => this.playerView.off(eventType, this.handlePlayerEvent));
    this.clearIntervals();
    this.initEvents();
  };

  _onSeek = () => {
    const { putToRecentsFinished } = this.props;

    if (putToRecentsFinished) {
      return this.sendInitialRecentsProgress();
    }
  };

  _onPlayerError = args => {
    const { setPlayerError } = this.props;
    const { code, type, name, data: { responseCode, responseText, serverResponse } = {} } = args;

    setPlayerError(code, responseCode, name, type, responseText, serverResponse);
    this.clearIntervals();
  };

  _onPlaybackFinished = () => {
    const {
      bingeWatching: { messageData, isBingeWatchingCanceled },
      analyticsBingeWatchingNoUserAction,
    } = this.props;

    if (!isBingeWatchingCanceled && messageData) {
      const {
        nextEpisode: {
          season_number: seasonNumber,
          episode_number: episodeNumber,
          episode_name: episodeName,
          franchise_name: franchiseName,
        },
      } = messageData;
      analyticsBingeWatchingNoUserAction(
        franchiseName || '',
        episodeName || '',
        seasonNumber || '',
        episodeNumber || '',
      );
    }

    if (this.isOnDemand) {
      this.playNextBingeEpisode();
      return;
    }

    if (!this.isLiveLinear) return;

    const {
      setPlayerStream,
      stream,
      nextQvtPayload,
      llEventTransition,
      airings,
      channelInfo,
    } = this.props;
    const { playback_info: playbackInfo } = nextQvtPayload;
    const { self } = nextQvtPayload;
    const { dash_manifest_url: dash, m3u8_url_template: hlsUrl } = playbackInfo;
    const { drm } = stream;
    const nextAiring = new AiringEventListing(
      first(airings.filter(airing => airing.qvt_url === self)),
    );
    const hls = hlsUrl.replace(
      'master_$version$_$target_duration$_$encryption_type$_$audio_type$',
      'master_7_3_fairplay',
    );
    const nextStream = {
      ...nextAiring._airing,
      drm,
      source: {
        dash,
        hls,
      },
      isLive: true,
      qvtUrl: self,
    };

    /* Chromecast receiver uses this flag to trigger source updating for LL content. */
    if (process.env.IN_CHROMECAST_MODE) nextStream.isLlStitching = true;

    const playerState = {
      volume: this.playerView.getVolume(),
      audio: this.playerView.getAudio().id,
      subtitle: this.playerView.subtitles.list().filter(subtitleTrack => subtitleTrack.enabled),
    };

    llEventTransition();

    setPlayerStream({
      stream: nextStream,
      sourceType: SourceType.LIVE_LINEAR,
      listing: nextAiring,
      contentId: nextAiring.contentIdentification?.echostar_id,
      channelInformation: channelInfo,
    });

    this.qvtHeartbeatFetch();
    this.playerView.setVolume(playerState.volume);
    this.playerView.setAudio(playerState.audio);

    if (playerState.subtitle && playerState.subtitle.length) {
      this.playerView.subtitles.enable(playerState.subtitle[0].id, true);
    }
  };

  readyEventExecuted = false;

  _onReady = () => {
    const { resumePlayback, stream } = this.props;
    const { progress_seconds: progressSeconds } = stream;

    if ((this.isOnDemand || this.isEst) && resumePlayback && progressSeconds.toString() !== 'NaN') {
      this.playerView.seek(progressSeconds);
    }
  };

  handlePlayerEvent = event => {
    const {
      onPlayerEvent,
      playerEvents,
      playerType,
      setPlaybackCouldNotBeStarted,
      setPlayerEventObject,
    } = this.props;

    setPlayerEventObject(event);

    if (this.events[event.type]) {
      this.events[event.type](event);
    }

    if (onPlayerEvent) {
      onPlayerEvent(event);
    }

    if (playerEvents && playerType === 'sling') {
      playerEvents.triggerEvent('bitmovinplayer', event);
    }

    if (event.type === 'playbackfinished') {
      if (!this.isLiveLinear) {
        this._onPlaybackFinished();
      }
      if (typeof this.managers.events.onPlaybackFinished === 'function') {
        this.managers.events.onPlaybackFinished();
      }
      this.readyEventExecuted = false;
    }

    if (event.type === 'sourceunloaded') {
      this.readyEventExecuted = false;
    }

    if (event.type === 'ready') {
      this._onReady();
      this.readyEventExecuted = true;
    }

    if (
      this.playerView &&
      event.type === PlayerEvent.Warning &&
      event.name === 'PLAYBACK_COULD_NOT_BE_STARTED'
    ) {
      setPlaybackCouldNotBeStarted(true);
    } else if (event.type === PlayerEvent.Playing) {
      setPlaybackCouldNotBeStarted(false);
    }
  };

  play = () => {
    if (this.playerView) {
      this.playerView.play();
    }
  };

  pause = () => {
    if (this.playerView) {
      this.playerView.pause();
    }
  };

  seek = seekTo => {
    if (this.playerView) {
      this.playerView.seek(seekTo);
    }
  };

  unload = () => {
    if (this.playerView) {
      this.playerView.unload();
    }
  };

  loadStream = () => {
    const { playerStreamLoaded } = this.props;
    if (this.playerView) {
      this.playerView
        .load(this.playerSource)
        .then(() => {
          playerStreamLoaded();
        })
        .catch(reason => {
          console.warn('Player exception: ', reason);
        });
    }
  };

  updateStream = () => {
    if (this.playerView) {
      this.loadStream();
    }
  };

  destroyPlayer = () => {
    // const { isNextBingeEpisodeLoading, resetPlayer } = this.props;
    if (this.playerView) {
      this.playerView.destroy();
      this.playerView = null;
    }
    this.clearIntervals();
    this.destroyBroadcast();

    // This line causes problems closing/switching players.
    // If we make it resetPlayer(false) then the player won't switch between EST/LL/OD and Slinging
    // But if we make it resetPlayer(true) then it won't close the player properly in the above types.
    // If we comment it, i believe it causes problems with Chromecast. Will need to re-evaluate a better way
    // to handle this down the road, but for now, the only option is to remove it.
    // MD 6/15/20
    // resetPlayer(true);
    // isNextBingeEpisodeLoading(false);
  };

  playNextBingeEpisode = async () => {
    const {
      franchise,
      franchiseSlug,
      stream,
      setPlayerStream,
      fetchVideoMetadata,
      // isNextBingeEpisodeLoading,
      bingeWatching: { isBingeWatchingCanceled },
    } = this.props;

    if (isBingeWatchingCanceled) return;

    const franchiseInfo = franchise[franchiseSlug] || {};
    const nextEpisode = getNextEpisode(franchiseInfo.episodes, stream.video_id);

    if (nextEpisode) {
      // isNextBingeEpisodeLoading(false);
      await fetchVideoMetadata(nextEpisode.video_id);
      this.initManagers();
      setPlayerStream({
        stream: nextEpisode.dyna_streams[0],
        sourceType: SourceType.ON_DEMAND,
      });
      cancelNextBingeEpisode(true);
    }
    this.playNextEpisodeCount += 1;
  };

  _onTimeChanged = ({ time: currentProgress }) => {
    /* Bingewatching functionality is not supported by Chromecast app */
    // if (process.env.IN_CHROMECAST_MODE) return;

    const {
      showBingeMessage,
      cancelNextBingeEpisode,
      bingeWatching: { messageData, isBingeWatchingCanceled },
      stream,
      sourceType,
      franchise,
      franchiseSlug,
      handleOnTimeChanged,
    } = this.props;

    // Pass to withPlayer
    handleOnTimeChanged(currentProgress);

    if (sourceType !== SourceType.ON_DEMAND) return;

    const franchiseInfo = franchise[franchiseSlug] || {};
    const nextEpisode = getNextEpisode(franchiseInfo.episodes, stream.video_id);

    const bitmovinPlayer = this.playerRef.current?.player;

    if (!nextEpisode || bitmovinPlayer?.ads?.getActiveAdBreak()) {
      if (messageData) {
        cancelNextBingeEpisode(true);
      }
      return;
    }

    const totalDuration = bitmovinPlayer && Math.floor(bitmovinPlayer.getDuration());

    const timeLeft = totalDuration - currentProgress;

    if (
      !isBingeWatchingCanceled &&
      totalDuration !== 0 &&
      timeLeft < BINGE_WATCHING_PERIOD &&
      timeLeft > 0
    ) {
      showBingeMessage(Math.floor(timeLeft), nextEpisode);
    } else if (messageData) {
      cancelNextBingeEpisode(true);
    }
  };

  setProgress = ({ time }) => {
    this.progress = Math.floor(time);
  };

  exitFullscreen = () => {
    const bitmovinPlayer = this.playerRef.current.player;
    if (bitmovinPlayer && typeof bitmovinPlayer.setViewMode !== 'undefined') {
      bitmovinPlayer.setViewMode('inline');
    }
  };

  getBingeWatchingMessageRightMargin = () => {
    const bitmovinPlayer = this.playerRef?.current?.player;
    if (!bitmovinPlayer) return 0;
    const width = document.getElementById('player').clientWidth;
    const height = document.getElementById('player').clientHeight;
    const aspectRatio = width / height;
    const videoAspectRatio =
      bitmovinPlayer.getVideoElement()?.videoWidth / bitmovinPlayer.getVideoElement()?.videoHeight;

    if (videoAspectRatio >= aspectRatio) return 10;

    return (width - height * videoAspectRatio) / 2 + 10;
  };

  render() {
    const { playerErrorCode } = this.props;

    if (playerErrorCode) {
      return <PlayerErrorMessage />;
    }

    return process.env.IS_BROWSER ? (
      <Box testID="player-container" style={() => styles.container} ref={this.containerRef}>
        <div id="player" className="html5-player" ref={this.playerRef} />
        <BingeWatchingMessage
          onPlayNow={this.playNextBingeEpisode}
          exitFullscreen={this.exitFullscreen}
          rightMargin={this.getBingeWatchingMessageRightMargin()}
        />
      </Box>
    ) : null;
  }
}

export { Player };

export default connect(
  state => ({
    radishUrl: state.config.session.radish_url,
    adaptUrl: state.config.adapt.url,
    networkDetails: state.network.network.details,
    airings: state.player.airings,
    viewer: state.auth.viewer,
    franchise: state.franchise.franchiseDetails.bySlug,
    franchiseSlug: state.found.match.params?.franchiseSlug || state.player?.airings[0]?.slug,
    browserFingerprint: state.device.deviceId,
    bingeWatching: state.player.bingeWatching,
    userIp: state.networkInfo.externalPublicIp,
    limitAdTracking: state.analytics.limitAdTracking,
  }),
  {
    cancelNextBingeEpisode,
    showBingeMessage,
    isNextBingeEpisodeLoading,
    analyticsBingeWatchingNoUserAction,
    analyticsNumberOfContinuousEpisodesWatched,
    setFranchiseProgress,
  },
)(withPlayer(Player));
